import { useQuery } from '@tanstack/react-query';
import { scimIntegrationApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

/** Rows per log page — the drawer's page-count arithmetic depends on it. */
export const SCIM_LOG_PAGE_SIZE = 25;

export function useScimIntegrations() {
  return useQuery({
    queryKey: queryKeys.scim.integrations(),
    queryFn: ({ signal }) =>
      scimIntegrationApiClient.list(toCancelToken(signal)),
    meta: { errorContext: 'SCIM integrations' },
  });
}

export function useScimUserMappings(integrationId: string | null) {
  return useQuery({
    queryKey: queryKeys.scim.userMappings(integrationId ?? ''),
    queryFn: ({ signal }) =>
      scimIntegrationApiClient.mappings(
        integrationId as string,
        toCancelToken(signal),
      ),
    enabled: !!integrationId,
    meta: { errorContext: 'SCIM user mappings' },
  });
}

export function useScimLogs(integrationId: string | null, page: number) {
  return useQuery({
    queryKey: queryKeys.scim.logs(integrationId ?? '', page),
    queryFn: ({ signal }) =>
      scimIntegrationApiClient.logs(
        integrationId as string,
        page,
        SCIM_LOG_PAGE_SIZE,
        toCancelToken(signal),
      ),
    enabled: !!integrationId,
    meta: { errorContext: 'SCIM audit logs' },
  });
}

export function useScimLogDetail(
  integrationId: string | null,
  logId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.scim.logDetail(integrationId ?? '', logId ?? ''),
    queryFn: ({ signal }) =>
      scimIntegrationApiClient.logDetail(
        integrationId as string,
        logId as string,
        toCancelToken(signal),
      ),
    enabled: !!integrationId && !!logId,
    // An audit row is immutable once written.
    staleTime: Infinity,
    meta: { errorContext: 'SCIM log detail' },
  });
}
